// src/services/sync.ts
// Offline sync queue for shopper ticks with background retry

import { api } from './api';

interface PendingUpdate {
  listId: string;
  itemId: string;
  checked: boolean;
  skipped: boolean;
  timestamp: number;
}

class SyncManager {
  private queue: PendingUpdate[] = [];
  private syncTimer: any = null;
  private isSyncing = false;
  private retryCount = 0;
  private onStatusChange?: (status: { active: boolean; syncing: boolean; error: string | null; count: number }) => void;

  constructor() {
    this.loadQueue();
    window.addEventListener('online', () => this.flush());
  }

  public registerStatusListener(cb: (status: { active: boolean; syncing: boolean; error: string | null; count: number }) => void) {
    this.onStatusChange = cb;
    this.notify();
  }

  private loadQueue() {
    try {
      const raw = localStorage.getItem('lms_offline_queue');
      if (raw) this.queue = JSON.parse(raw);
    } catch {
      this.queue = [];
    }
  }

  private saveQueue() {
    localStorage.setItem('lms_offline_queue', JSON.stringify(this.queue));
    this.notify();
  }

  private notify(syncing = false, error: string | null = null) {
    if (this.onStatusChange) {
      this.onStatusChange({
        active: this.queue.length > 0,
        syncing,
        error,
        count: this.queue.length,
      });
    }
  }

  public queueItemUpdate(listId: string, itemId: string, checked: boolean, skipped = false) {
    // Remove previous pending update for same item
    this.queue = this.queue.filter(q => !(q.listId === listId && q.itemId === itemId));
    this.queue.push({ listId, itemId, checked, skipped, timestamp: Date.now() });
    this.saveQueue();
  }

  public hasPending(listId?: string): boolean {
    if (!listId) return this.queue.length > 0;
    return this.queue.some(q => q.listId === listId);
  }

  public async flush(): Promise<boolean> {
    if (this.queue.length === 0 || this.isSyncing) return true;

    this.isSyncing = true;
    this.notify(true, null);

    // Group updates by listId
    const byList: Record<string, PendingUpdate[]> = {};
    for (const item of this.queue) {
      if (!byList[item.listId]) byList[item.listId] = [];
      byList[item.listId].push(item);
    }

    try {
      for (const [listId, items] of Object.entries(byList)) {
        await api.updateItems(
          listId,
          items.map(i => ({ id: i.itemId, checked: i.checked, skipped: i.skipped }))
        );
      }

      this.queue = [];
      this.saveQueue();
      this.retryCount = 0;
      this.isSyncing = false;
      this.notify(false, null);
      return true;
    } catch (err: any) {
      this.isSyncing = false;
      this.retryCount++;
      const delay = Math.min(5000 * Math.pow(1.5, this.retryCount), 60000);
      this.notify(false, `Offline — retrying in ${Math.round(delay / 1000)}s`);

      clearTimeout(this.syncTimer);
      this.syncTimer = setTimeout(() => this.flush(), delay);
      return false;
    }
  }
}

export const syncManager = new SyncManager();
